import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatNumber, formatDateTime, formatTimeAgo, formatDate, formatTime } from '../utils/formatters';

const priorityColor = p => p === 'high' ? 'var(--danger)' : p === 'medium' ? 'var(--warning)' : 'var(--text-light)';
const statusBadgeClass = s => s === 'completed' ? 'badge-success' : s === 'cancelled' ? 'badge-danger' : s === 'live' ? 'badge-info' : 'badge-warning';

export default function Home() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [bulletins, setBulletins] = useState([]);
  const [bulletinBody, setBulletinBody] = useState('');
  const [bulletinUsers, setBulletinUsers] = useState([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionCursorPos, setMentionCursorPos] = useState(0);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [bulletinVisibility, setBulletinVisibility] = useState('all');
  const [bulletinRoles, setBulletinRoles] = useState([]);
  const [expandedBulletins, setExpandedBulletins] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const bulletinInputRef = useRef(null);
  const pendingCursorPos = useRef(null);
  const canPostBulletin = user && ['admin', 'partner'].includes(user.role);
  const AVAILABLE_ROLES = ['admin', 'partner', 'buyer', 'view_only'];

  // Apply pending cursor position after React re-renders the textarea
  useEffect(() => {
    if (pendingCursorPos.current !== null) {
      const el = bulletinInputRef.current;
      if (el) { el.focus(); el.setSelectionRange(pendingCursorPos.current, pendingCursorPos.current); }
      pendingCursorPos.current = null;
    }
  }, [bulletinBody]);

  const [error, setError] = useState(null);

  // My Commission cell — only rendered for users who have hosted a break.
  const [commission, setCommission] = useState(null);
  const [commissionMonth, setCommissionMonth] = useState(null); // null → server picks the current month
  const [commissionLoading, setCommissionLoading] = useState(true);
  const [commissionOpen, setCommissionOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const { data: d } = await api.get('/api/home', { params: { allActivity: showAllActivity } });
      setData(d);
      setBulletins(d.bulletins || []);
    } catch (err) {
      console.error('Home fetch error:', err);
      setError('Failed to load dashboard data. Please try again.');
    }
  }, [showAllActivity]);

  const fetchBulletins = fetchData;

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (canPostBulletin) {
      api.get('/api/users').then(({ data: d }) => setBulletinUsers(d.users)).catch(() => {});
    }
  }, [canPostBulletin]);

  const fetchCommission = useCallback(async () => {
    setCommissionLoading(true);
    try {
      const { data: d } = await api.get('/api/home/my-commission', {
        params: commissionMonth ? { month: commissionMonth } : {},
      });
      setCommission(d);
    } catch (err) {
      console.error('My commission fetch error:', err);
    } finally {
      setCommissionLoading(false);
    }
  }, [commissionMonth]);

  useEffect(() => { fetchCommission(); }, [fetchCommission]);

  if (error) return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <p style={{ color: 'var(--danger)', marginBottom: '12px' }}>{error}</p>
      <button className="btn btn-primary" onClick={fetchData}>Retry</button>
    </div>
  );
  if (!data) return <div className="loading-screen"><div className="spinner" /></div>;

  const isFinancial = ['admin', 'partner'].includes(data.role);
  const greeting = getGreeting();

  function handleBulletinInput(e) {
    const val = e.target.value;
    setBulletinBody(val);
    const cursorPos = e.target.selectionStart;
    setMentionCursorPos(cursorPos);

    // Remove mentions whose @name is no longer in the text
    setSelectedMentions(prev => prev.filter(m => val.includes(`@${m.name}`)));

    // Check if we're typing an @mention (support names with spaces)
    const textBeforeCursor = val.substring(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@([\w\s]*)$/);
    if (atMatch && !atMatch[1].endsWith('  ')) {
      setMentionSearch(atMatch[1].trim().toLowerCase());
      setShowMentionDropdown(true);
    } else {
      setShowMentionDropdown(false);
    }
  }

  function insertMention(u) {
    const textBeforeCursor = bulletinBody.substring(0, mentionCursorPos);
    const textAfterCursor = bulletinBody.substring(mentionCursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    const newText = textBeforeCursor.substring(0, atIndex) + `@${u.name} ` + textAfterCursor;
    setBulletinBody(newText);
    if (!selectedMentions.find(m => m.id === u.id)) {
      setSelectedMentions([...selectedMentions, u]);
    }
    setShowMentionDropdown(false);
    pendingCursorPos.current = atIndex + 1 + u.name.length + 1; // @ + name + space
  }

  function renderHighlightedBody(text) {
    if (!text) return '';
    if (selectedMentions.length === 0) return <span style={{ color: 'var(--text)' }}>{text}</span>;
    const parts = [];
    let remaining = text;
    let key = 0;
    while (remaining) {
      let earliest = -1, earliestMention = null;
      for (const m of selectedMentions) {
        const idx = remaining.indexOf(`@${m.name}`);
        if (idx !== -1 && (earliest === -1 || idx < earliest)) {
          earliest = idx;
          earliestMention = m;
        }
      }
      if (earliest === -1 || !earliestMention) { parts.push(<span key={key++} style={{ color: 'var(--text)' }}>{remaining}</span>); break; }
      if (earliest > 0) parts.push(<span key={key++} style={{ color: 'var(--text)' }}>{remaining.substring(0, earliest)}</span>);
      const tag = `@${earliestMention.name}`;
      parts.push(<span key={key++} style={{ color: 'var(--primary)', fontWeight: 600 }}>{tag}</span>);
      remaining = remaining.substring(earliest + tag.length);
    }
    return parts;
  }

  async function postBulletin(e) {
    e.preventDefault();
    if (!bulletinBody.trim()) return;
    try {
      await api.post('/api/bulletins', {
        body: bulletinBody,
        mentions: selectedMentions.map(m => m.id),
        visibility: bulletinVisibility,
        visible_roles: bulletinVisibility === 'role' ? bulletinRoles : []
      });
      setBulletinBody('');
      setSelectedMentions([]);
      setBulletinVisibility('all');
      setBulletinRoles([]);
      fetchBulletins();
    } catch (err) {
      console.error('Post bulletin error:', err);
      alert('Failed to post bulletin');
    }
  }

  async function deleteBulletin(id) {
    if (!window.confirm('Delete this bulletin?')) return;
    await api.delete(`/api/bulletins/${id}`);
    fetchBulletins();
  }

  async function toggleComments(bulletinId) {
    if (expandedBulletins[bulletinId]) {
      setExpandedBulletins(prev => { const n = { ...prev }; delete n[bulletinId]; return n; });
      return;
    }
    setLoadingComments(prev => ({ ...prev, [bulletinId]: true }));
    try {
      const { data: d } = await api.get(`/api/bulletins/${bulletinId}/comments`);
      setExpandedBulletins(prev => ({ ...prev, [bulletinId]: d.comments }));
    } catch (err) { console.error('Load comments error:', err); }
    setLoadingComments(prev => ({ ...prev, [bulletinId]: false }));
  }

  async function postComment(bulletinId) {
    const body = commentInputs[bulletinId]?.trim();
    if (!body) return;
    try {
      await api.post(`/api/bulletins/${bulletinId}/comments`, { body });
      setCommentInputs(prev => ({ ...prev, [bulletinId]: '' }));
      const { data: d } = await api.get(`/api/bulletins/${bulletinId}/comments`);
      setExpandedBulletins(prev => ({ ...prev, [bulletinId]: d.comments }));
      setBulletins(prev => prev.map(b => b.id === bulletinId ? { ...b, comment_count: d.comments.length } : b));
    } catch (err) {
      console.error('Post comment error:', err);
      alert('Failed to post comment');
    }
  }

  const filteredMentionUsers = bulletinUsers.filter(u =>
    u.id !== user.id && (!mentionSearch || u.name.toLowerCase().includes(mentionSearch))
  );

  return (
    <div>
      {/* Welcome Header */}
      <div style={{ marginBottom: '16px' }}>
        <h2 className="page-title" style={{ marginBottom: '2px' }}>{greeting}, {user.name.split(' ')[0]}</h2>
        <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' })}
        </div>
      </div>

      {/* Bulletin Board */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 className="card-title" style={{ marginBottom: '12px' }}>Bulletin Board</h3>

        {canPostBulletin && (
          <form onSubmit={postBulletin} style={{ marginBottom: bulletins.length > 0 ? '16px' : 0, position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <div
                aria-hidden="true"
                style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, padding: '10px', fontSize: '13px', fontFamily: 'inherit', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordWrap: 'break-word', pointerEvents: 'none', borderRadius: '6px', border: '1px solid transparent', overflow: 'hidden' }}
              >
                {renderHighlightedBody(bulletinBody)}
              </div>
              <textarea
                ref={bulletinInputRef}
                value={bulletinBody}
                onChange={handleBulletinInput}
                placeholder="Post an update... Use @name to mention users."
                style={{ width: '100%', minHeight: '60px', resize: 'vertical', fontSize: '13px', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', fontFamily: 'inherit', lineHeight: '1.5', background: 'transparent', color: 'transparent', caretColor: 'var(--text)' }}
              />
              {showMentionDropdown && filteredMentionUsers.length > 0 && (
                <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: 'white', border: '1px solid var(--border)', borderRadius: '6px', boxShadow: 'var(--shadow-lg)', maxHeight: '140px', overflowY: 'auto', zIndex: 50 }}>
                  {filteredMentionUsers.map(u => (
                    <div key={u.id} onClick={() => insertMention(u)}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}>
                      <div style={{ fontWeight: 500 }}>{u.name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{u.email}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedMentions.length > 0 && (
              <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
                Mentioned: {selectedMentions.map(m => m.name).join(', ')}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Visible to:</label>
                <select value={bulletinVisibility} onChange={e => setBulletinVisibility(e.target.value)}
                  style={{ fontSize: '12px', padding: '4px 8px' }}>
                  <option value="all">All Users</option>
                  <option value="role">Specific Roles</option>
                  <option value="mentioned">Mentioned Only</option>
                </select>
              </div>
              {bulletinVisibility === 'role' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  {AVAILABLE_ROLES.map(r => (
                    <label key={r} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={bulletinRoles.includes(r)}
                        style={{ width: '14px', height: '14px', flexShrink: 0 }}
                        onChange={e => {
                          if (e.target.checked) setBulletinRoles([...bulletinRoles, r]);
                          else setBulletinRoles(bulletinRoles.filter(x => x !== r));
                        }} />
                      {r.replace('_', ' ')}
                    </label>
                  ))}
                </div>
              )}
              <div style={{ marginLeft: 'auto' }}>
                <button type="submit" className="btn btn-primary btn-sm"
                  disabled={!bulletinBody.trim() || (bulletinVisibility === 'role' && bulletinRoles.length === 0)}>
                  Post
                </button>
              </div>
            </div>
          </form>
        )}

        {bulletins.length === 0 ? (
          <p style={{ color: 'var(--text-light)', fontSize: '13px' }}>No bulletins yet.</p>
        ) : (
          bulletins.map(b => (
            <div key={b.id} style={{ padding: '12px 0', borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                {/* Expand arrow */}
                <button onClick={() => toggleComments(b.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 8px 0 0',
                    fontSize: '10px', color: 'var(--text-light)', flexShrink: 0 }}>
                  {loadingComments[b.id] ? '...' : expandedBulletins[b.id] ? '▼' : '▶'}
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <strong style={{ fontSize: '13px' }}>{b.author_name}</strong>
                        <span style={{ fontSize: '11px', color: 'var(--text-light)' }}>{formatTimeAgo(b.created_at)}</span>
                        {b.visibility === 'mentioned' && (
                          <span className="badge badge-info" style={{ fontSize: '10px' }}>Mentioned Only</span>
                        )}
                        {b.visibility === 'role' && b.visible_roles && (
                          <span className="badge badge-warning" style={{ fontSize: '10px' }}>
                            {JSON.parse(b.visible_roles).map(r => r.replace('_', ' ')).join(', ')}
                          </span>
                        )}
                        {b.comment_count > 0 && (
                          <span style={{ fontSize: '11px', color: 'var(--text-light)', cursor: 'pointer' }}
                            onClick={() => toggleComments(b.id)}>
                            {b.comment_count} comment{b.comment_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                        {renderBulletinBody(b.body, b.mentions)}
                      </div>
                    </div>
                    {(user.role === 'admin' || b.created_by === user.id) && (
                      <button onClick={() => deleteBulletin(b.id)}
                        style={{ background: 'none', border: 'none', color: 'var(--text-light)', cursor: 'pointer', fontSize: '16px', padding: '0 4px', lineHeight: 1 }}
                        title="Delete bulletin">&times;</button>
                    )}
                  </div>

                  {/* Expanded comments */}
                  {expandedBulletins[b.id] && (
                    <div style={{ marginTop: '10px', borderLeft: '2px solid var(--border)', paddingLeft: '12px' }}>
                      {expandedBulletins[b.id].length > 0 ? expandedBulletins[b.id].map(c => (
                        <div key={c.id} style={{ marginBottom: '8px', fontSize: '13px' }}>
                          <strong style={{ fontSize: '12px' }}>{c.author_name}</strong>
                          <span style={{ color: 'var(--text-light)', fontSize: '11px', marginLeft: '6px' }}>
                            {formatTimeAgo(c.created_at)}
                          </span>
                          <div style={{ marginTop: '2px', whiteSpace: 'pre-wrap' }}>{c.body}</div>
                        </div>
                      )) : (
                        <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '8px' }}>No comments yet.</div>
                      )}
                      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                        <input
                          value={commentInputs[b.id] || ''}
                          onChange={e => setCommentInputs(prev => ({ ...prev, [b.id]: e.target.value }))}
                          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), postComment(b.id))}
                          placeholder="Write a comment..."
                          style={{ flex: 1, fontSize: '12px', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '4px', fontFamily: 'inherit' }}
                        />
                        <button onClick={() => postComment(b.id)} className="btn btn-primary btn-sm"
                          disabled={!commentInputs[b.id]?.trim()}>Reply</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Quick Stats */}
      <div className="kpi-grid home-quick-stats" style={{ marginBottom: '24px' }}>
        {isFinancial ? (
          <>
            <StatCard label="Today's GMV" value={formatCurrency(data.quickStats.todayGmv)} link="/financials" />
            <StatCard label="Today's Orders" value={formatNumber(data.quickStats.todayOrders)} link="/orders" />
            <StatCard label="Pending Tasks" value={data.quickStats.pendingTasks} link="/tasks" />
            <StatCard label="Planned Breaks" value={data.quickStats.plannedBreaks} link="/breaks" />
          </>
        ) : (
          <>
            <StatCard label="My Open Tasks" value={data.quickStats.myOpenTasks || 0} link="/tasks" />
            <StatCard label="My Breaks This Week" value={data.quickStats.myBreaksThisWeek || 0} link="/breaks" />
            <StatCard label="Unread Notifications" value={data.unreadCount} link="/notifications-config" />
          </>
        )}
      </div>

      <div className="home-grid">
        {/* Left Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* My Commission — same numbers as the admin Breaker Commission page,
              scoped server-side to this user. Hidden for anyone who has never
              hosted a completed or pending break. */}
          {commission?.isBreaker && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">My Commission</h3>
                <select
                  value={commissionMonth || commission.month}
                  onChange={e => setCommissionMonth(e.target.value)}
                  aria-label="Commission month"
                  style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid var(--border)' }}>
                  {commission.months.map(m => <option key={m} value={m}>{formatMonthLabel(m)}</option>)}
                </select>
              </div>
              <div style={{ opacity: commissionLoading ? 0.5 : 1, transition: 'opacity 0.15s' }}>
                <div className="kpi-value">{formatCurrency(commission.commission)}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>
                  {commission.breakCount} break{commission.breakCount === 1 ? '' : 's'} hosted
                  {' · '}{formatDate(commission.range.start)} – {formatDate(commission.range.end)}
                </div>
                {commission.breaks.length > 0 && (
                  <>
                    <button onClick={() => setCommissionOpen(o => !o)}
                      style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '12px', padding: '8px 0 0' }}>
                      {commissionOpen ? '▼ Hide breaks' : '▶ Show breaks'}
                    </button>
                    {commissionOpen && (
                      <div className="table-container" style={{ marginTop: '8px' }}>
                        <table style={{ width: '100%', fontSize: '12px' }}>
                          <thead>
                            <tr style={{ color: 'var(--text-light)' }}>
                              <th style={{ textAlign: 'left', padding: '4px 6px' }}>Break #</th>
                              <th style={{ textAlign: 'left', padding: '4px 6px' }}>Date</th>
                              <th style={{ textAlign: 'left', padding: '4px 6px' }}>Name</th>
                              <th style={{ textAlign: 'right', padding: '4px 6px' }}>Net Sales</th>
                              <th style={{ textAlign: 'right', padding: '4px 6px' }}>Net Profit</th>
                              <th style={{ textAlign: 'right', padding: '4px 6px' }}>Commission</th>
                            </tr>
                          </thead>
                          <tbody>
                            {commission.breaks.map(br => (
                              <tr key={br.id}>
                                <td style={{ padding: '3px 6px' }}>{br.break_number || '--'}</td>
                                <td style={{ padding: '3px 6px' }}>{formatDate(br.date)}</td>
                                <td style={{ padding: '3px 6px' }}>{br.name}</td>
                                <td style={{ padding: '3px 6px', textAlign: 'right' }}>{formatCurrency(br.net_sales)}</td>
                                <td style={{ padding: '3px 6px', textAlign: 'right', color: br.net_profit < 0 ? 'var(--danger)' : undefined }}>{formatCurrency(br.net_profit)}</td>
                                <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(br.commission)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Notifications */}
          {data.notifications.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Unread Notifications</h3>
                {data.unreadCount > 5 && <span className="badge badge-default">{data.unreadCount} total</span>}
              </div>
              {data.notifications.map(n => (
                <Link key={n.id} to={n.link || '#'} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div style={{ padding: '10px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '13px' }}>{n.title}</div>
                      {n.description && <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '2px' }}>{n.description}</div>}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-light)', whiteSpace: 'nowrap', marginLeft: '12px' }}>{formatTimeAgo(n.created_at)}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* My Tasks */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                My Tasks
                {data.overdueCount > 0 && <span className="badge badge-danger" style={{ marginLeft: '8px' }}>{data.overdueCount} overdue</span>}
              </h3>
              <Link to="/tasks" className="btn btn-secondary btn-sm">View All</Link>
            </div>
            {data.tasks.length === 0 ? (
              <p style={{ color: 'var(--text-light)', fontSize: '13px' }}>No open tasks. You're all caught up!</p>
            ) : (
              data.tasks.map(t => (
                <Link key={t.id} to="/tasks" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div style={{ padding: '10px 0', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '4px', height: '28px', borderRadius: '2px', background: priorityColor(t.priority), flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: '13px' }}>{t.title}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)', display: 'flex', gap: '8px', marginTop: '2px' }}>
                        <span className={`badge ${t.status === 'in_progress' ? 'badge-info' : 'badge-default'}`} style={{ fontSize: '10px' }}>{t.status}</span>
                        {t.due_date && <span style={{ color: t.due_date < data.today ? 'var(--danger)' : 'inherit' }}>Due {formatDate(t.due_date)}</span>}
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>

          {/* Recent Mentions */}
          {data.recentMentions.length > 0 && (
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '12px' }}>Recent Mentions</h3>
              {data.recentMentions.map((m, i) => (
                <Link key={i} to={`/${m.entity_type}s/${m.entity_id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>
                    <strong>{m.author_name}</strong> mentioned you in a {m.entity_type}
                    <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '2px' }}>
                      "{m.body.length > 80 ? m.body.substring(0, 80) + '...' : m.body}"
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '2px' }}>{formatTimeAgo(m.created_at)}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Today's Breaks */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Today's Breaks</h3>
              <Link to="/breaks" className="btn btn-secondary btn-sm">View All</Link>
            </div>
            {data.todayBreaks.length === 0 ? (
              <p style={{ color: 'var(--text-light)', fontSize: '13px' }}>No breaks scheduled for today.</p>
            ) : (
              data.todayBreaks.map(b => (
                <Link key={b.id} to="/breaks" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div style={{ padding: '10px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '13px' }}>#{b.break_number} {b.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '2px' }}>
                        {b.scheduled_time ? formatTime(b.scheduled_time) : 'No time set'} &middot; {b.host_name || 'Unassigned'} &middot; {b.product_count} products
                      </div>
                    </div>
                    <span className={`badge ${statusBadgeClass(b.status)}`}>{b.status}</span>
                  </div>
                </Link>
              ))
            )}
          </div>

          {/* Upcoming Breaks */}
          {data.upcomingBreaks.length > 0 && (
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '12px' }}>Upcoming Breaks</h3>
              {data.upcomingBreaks.map(b => (
                <Link key={b.id} to="/breaks" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '13px' }}>#{b.break_number} {b.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '2px' }}>
                        {formatDate(b.date)} {b.scheduled_time ? `at ${formatTime(b.scheduled_time)}` : ''} &middot; {b.host_name || 'Unassigned'}
                      </div>
                    </div>
                    <span className={`badge ${statusBadgeClass(b.status)}`}>{b.status}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Recent Activity */}
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '12px' }}>
              Recent Activity{showAllActivity ? ' (All Users)' : ''}
            </h3>
            {data.recentActivity.length === 0 ? (
              <p style={{ color: 'var(--text-light)', fontSize: '13px' }}>No recent activity.</p>
            ) : (
              data.recentActivity.map(a => (
                <div key={a.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '13px', display: 'flex', justifyContent: 'space-between' }}>
                  <span>
                    <strong>{a.user_name || 'System'}</strong>{' '}
                    {formatAction(a.action)}
                    {a.entity_type && a.entity_id ? (
                      <Link to={getEntityLink(a.entity_type, a.link_id || a.entity_id)} style={{ color: 'var(--accent)', marginLeft: '4px' }}>
                        {a.entity_label || `${a.entity_type} #${a.entity_id}`}
                      </Link>
                    ) : a.entity_type ? ` on ${a.entity_type}` : ''}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--text-light)', whiteSpace: 'nowrap', marginLeft: '8px' }}>{formatTimeAgo(a.created_at)}</span>
                </div>
              ))
            )}
            {data.role === 'admin' && (
              <div style={{ marginTop: '8px' }}>
                <button onClick={() => setShowAllActivity(!showAllActivity)}
                  style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '12px', padding: 0 }}>
                  {showAllActivity ? 'Show Only My Activity' : 'See Recent Activity Across All Users'}
                </button>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Quick Actions</h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {isFinancial && <Link to="/financials" className="btn btn-primary btn-sm">View Financials</Link>}
              <Link to="/breaks" className="btn btn-secondary btn-sm">View Breaks</Link>
              <Link to="/tasks" className="btn btn-secondary btn-sm">View Tasks</Link>
              <Link to="/orders" className="btn btn-secondary btn-sm">View Orders</Link>
              {['admin', 'partner'].includes(data.role) && <Link to="/breaks" className="btn btn-secondary btn-sm">New Break</Link>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, link }) {
  const content = (
    <div className="kpi-card" style={{ cursor: link ? 'pointer' : 'default' }}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
  return link ? <Link to={link} style={{ textDecoration: 'none', color: 'inherit' }}>{content}</Link> : content;
}

// "2026-09" → "September 2026", for the commission month picker.
function formatMonthLabel(month) {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function formatAction(action) {
  return action.replace(/_/g, ' ');
}

function renderBulletinBody(body, mentions = []) {
  if (!mentions || mentions.length === 0) return body;
  const parts = [];
  let remaining = body;
  for (const m of mentions) {
    const tag = `@${m.name}`;
    const idx = remaining.indexOf(tag);
    if (idx !== -1) {
      if (idx > 0) parts.push(remaining.substring(0, idx));
      parts.push(<span key={m.mentioned_user_id} style={{ color: 'var(--primary)', fontWeight: 600 }}>{tag}</span>);
      remaining = remaining.substring(idx + tag.length);
    }
  }
  if (remaining) parts.push(remaining);
  return parts.length > 0 ? parts : body;
}

function getEntityLink(entityType, entityId) {
  switch (entityType) {
    case 'break': return `/breaks/${entityId}`;
    case 'task': return `/tasks/${entityId}`;
    case 'order': return `/orders/${entityId}`;
    default: return '#';
  }
}
